/**
 * Controllo del codice fiscale delle persone fisiche.
 *
 * Il codice finisce sugli attestati dei corsi: un carattere sbagliato vuol dire
 * un attestato da rifare. Per questo non basta la forma (16 caratteri): si
 * controlla anche l'ultima lettera, che e' calcolata dalle altre quindici e
 * scopre quasi ogni errore di battitura.
 *
 * Nessuna dipendenza: lo usano sia la pagina del modulo sia la funzione che lo
 * riceve, e devono dare la stessa risposta.
 */

/**
 * Le cifre possono essere sostituite da lettere quando due persone avrebbero
 * lo stesso codice (omocodia): questa e' la tabella di sostituzione.
 */
const OMOCODIA = 'LMNPQRSTUV';

const FORMA =
  /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;

/** Valore dei caratteri in posizione dispari (1a, 3a, 5a...) per il carattere di controllo. */
const DISPARI: Record<string, number> = {
  0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
  A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
  K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14,
  U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
};

/** Lettera del mese di nascita, gennaio = A. */
const MESI = 'ABCDEHLMPRST';

/** Maiuscolo e senza spazi: e' cosi' che il codice va salvato. */
export function normalizzaCodiceFiscale(valore: string): string {
  return valore.replace(/\s+/g, '').toUpperCase();
}

function valorePari(carattere: string): number {
  return /[0-9]/.test(carattere) ? Number(carattere) : carattere.charCodeAt(0) - 65;
}

function carattereDiControllo(primiQuindici: string): string {
  let somma = 0;
  for (let i = 0; i < 15; i++) {
    const c = primiQuindici[i];
    somma += i % 2 === 0 ? DISPARI[c] : valorePari(c);
  }
  return String.fromCharCode(65 + (somma % 26));
}

/** Vero se il codice ha la forma giusta e l'ultima lettera torna. */
export function codiceFiscaleValido(valore: string): boolean {
  const cf = normalizzaCodiceFiscale(valore);
  if (!FORMA.test(cf)) return false;
  return carattereDiControllo(cf.slice(0, 15)) === cf[15];
}

/** Riporta a cifra un carattere eventualmente sostituito per omocodia. */
function cifra(carattere: string): string {
  const posizione = OMOCODIA.indexOf(carattere);
  return posizione >= 0 ? String(posizione) : carattere;
}

/**
 * Vero se la data di nascita (yyyy-mm-dd) e' quella scritta dentro il codice.
 * Del codice si guardano anno (ultime due cifre), mese e giorno; per le donne
 * il giorno e' aumentato di 40.
 *
 * Da chiamare solo su un codice gia' riconosciuto valido.
 */
export function dataNascitaCoincide(codice: string, dataNascita: string): boolean {
  const cf = normalizzaCodiceFiscale(codice);
  const [anno, mese, giorno] = dataNascita.split('-').map(Number);
  if (!anno || !mese || !giorno) return false;

  const annoCf = Number(cifra(cf[6]) + cifra(cf[7]));
  const meseCf = MESI.indexOf(cf[8]) + 1;
  const giornoGrezzo = Number(cifra(cf[9]) + cifra(cf[10]));
  const giornoCf = giornoGrezzo > 40 ? giornoGrezzo - 40 : giornoGrezzo;

  return annoCf === anno % 100 && meseCf === mese && giornoCf === giorno;
}
